import { addMonths, lastDayOfMonth, setDate } from 'date-fns';

export type DueDateType =
  | 'days-from-invoice'
  | 'end-of-following-month'
  | 'day-of-following-month'
  | 'days-after-end-of-month';

/**
 * Maps the raw `dueType` string returned by the Financials GraphQL API
 * (enum DueDaysType) to the frontend DueDateType.
 *
 * GraphQL enum values: DaysFromInvoice, EndOfFollowingMonth,
 * DayOfFollowingMonth, DaysAfterEndOfMonth.
 */
export function mapApiDueType(apiValue: string | null | undefined): DueDateType {
  switch (apiValue) {
    case 'DaysFromInvoice':
    case 'Days':
      return 'days-from-invoice';
    case 'EndOfFollowingMonth':
      return 'end-of-following-month';
    case 'DayOfFollowingMonth':
      return 'day-of-following-month';
    case 'DaysAfterEndOfMonth':
      return 'days-after-end-of-month';
    default:
      return 'days-from-invoice';
  }
}

/**
 * Calculates the invoice due date based on the customer's payment terms.
 *
 * @param invoiceDate  The invoice date
 * @param dueType      How the due date should be computed
 * @param dueDays      Number of days (interpretation depends on dueType)
 */
export function calculateDueDate(
  invoiceDate: Date,
  dueType: DueDateType,
  dueDays: number,
): Date {
  switch (dueType) {
    case 'days-from-invoice':
      return new Date(invoiceDate.getTime() + dueDays * 24 * 60 * 60 * 1000);

    case 'end-of-following-month':
      return lastDayOfMonth(addMonths(invoiceDate, 1));

    case 'day-of-following-month': {
      const nextMonth = addMonths(invoiceDate, 1);
      const lastDay = lastDayOfMonth(nextMonth).getDate();
      const clampedDay = Math.min(dueDays, lastDay);
      return setDate(nextMonth, clampedDay);
    }

    case 'days-after-end-of-month': {
      const endOfMonth = lastDayOfMonth(invoiceDate);
      return new Date(endOfMonth.getTime() + dueDays * 24 * 60 * 60 * 1000);
    }

    default:
      return new Date(invoiceDate.getTime() + dueDays * 24 * 60 * 60 * 1000);
  }
}
